import { useState, useEffect } from "react";
import AdLink from "@/components/AdLink";
import { Megaphone } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Ad } from "@/pages/AdminAdsPage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useActiveAds } from "@/hooks/useActiveAds";
import { getSafeImageUrl } from "@/utils/sanitizeUrl";

export default function AdPopupModal() {
  const { lang } = useI18n();
  const activePopupAds = useActiveAds('popup');
  const popupAd = activePopupAds.length > 0 ? activePopupAds[0] : null;
  
  const [open, setOpen] = useState(false);

  // To prevent the modal from popping up infinitely based on hook updates,
  // we'll track the ID of the ad we've shown in this specific component lifecycle.
  const [shownAdId, setShownAdId] = useState<string | null>(null);

  useEffect(() => {
    if (popupAd && popupAd.id !== shownAdId) {
        const closedAds = JSON.parse(sessionStorage.getItem("kaynin_closed_ads") || "[]");
        if (!closedAds.includes(popupAd.id)) {
            setOpen(true);
            setShownAdId(popupAd.id);
        }
    }
  }, [popupAd, shownAdId]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen && popupAd) {
      const closedAds = JSON.parse(sessionStorage.getItem("kaynin_closed_ads") || "[]");
      if (!closedAds.includes(popupAd.id)) {
        closedAds.push(popupAd.id);
        sessionStorage.setItem("kaynin_closed_ads", JSON.stringify(closedAds));
      }
    }
  };

  useEffect(() => {
    // If ad updates in admin panel, force re-show
    const handlePreview = () => setShownAdId(null);
    window.addEventListener("kayninAdsUpdated", handlePreview);
    return () => window.removeEventListener("kayninAdsUpdated", handlePreview);
  }, []);

  if (!popupAd) return null;
  const title = lang === "fr" ? popupAd.titleFr : lang === "ar" ? popupAd.titleAr : popupAd.titleZgh || popupAd.titleFr;
  const description = lang === "fr" ? popupAd.descriptionFr : lang === "ar" ? popupAd.descriptionAr : popupAd.descriptionZgh || popupAd.descriptionFr;
  const cta = lang === "fr" ? "Découvrir" : lang === "ar" ? "اكتشف" : "ⵉⵙⵙⵏ";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`sm:max-w-${popupAd.popupSize || 'md'} border-t-8 ${popupAd.bgColor.replace('bg-', 'border-t-')} z-[110] overflow-hidden`}>
        {popupAd.imageUrl && getSafeImageUrl(popupAd.imageUrl) && (
           <div className="-mx-6 -mt-6 mb-4 relative">
             <img src={getSafeImageUrl(popupAd.imageUrl)} alt="Popup visual" className="w-full h-48 object-cover" />
           </div>
        )}
        <DialogHeader>
          {(!popupAd.imageUrl || !getSafeImageUrl(popupAd.imageUrl)) && (
            <div className={`mx-auto w-12 h-12 rounded-full ${popupAd.bgColor} text-white flex items-center justify-center mb-4`}>
              <Megaphone className="w-6 h-6" />
            </div>
          )}
          {title && <DialogTitle className="text-center font-display text-xl leading-tight text-balance">{title}</DialogTitle>}
          {description && (
            <DialogDescription 
              className={`text-center font-body mt-2 text-balance ${popupAd.popupSize === 'lg' || popupAd.popupSize === 'xl' ? 'text-base' : 'text-sm'} ${popupAd.textFormat === 'bold' || popupAd.textFormat === 'bold-italic' ? 'font-bold text-foreground' : ''} ${popupAd.textFormat === 'italic' || popupAd.textFormat === 'bold-italic' ? 'italic' : ''}`}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </DialogHeader>
        {popupAd.linkUrl && (
          <div className="flex justify-center mt-4">
            <Button asChild className={`${popupAd.bgColor} text-white font-bold px-8 hover:opacity-90`}>
              <AdLink href={popupAd.linkUrl} onClick={() => handleOpenChange(false)}>{cta}</AdLink>
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
